import { useLoggerContext } from 'contexts/LoggerContext'
import { GraphQLError } from 'graphql'
import { reportErrorContext } from 'lib/bugsnag'
import { useCallback, useMemo } from 'react'
import { useAmplitude } from 'react-amplitude-hooks'

/*
  A Logging Sink where we could log the same event to multiple providers.
  Don't use this as a replacement for console.log during general debugging though
  as that would send all those logs to amplitude. 
*/

export type LoggerEvent =
  | 'Change cart item quantity'
  | 'Changed payment type'
  | 'Clicked on Add a new card button'
  | 'Clicked on pay now button'
  | 'Clicked on Start a new tab button'
  | 'Closing Apple/Google pay modal without paying'
  | 'Eonx check in failure'
  | 'Eonx check in ID found'
  | 'Eonx check in success'
  | 'Fetching payment intent with total'
  | 'New card Added'
  | 'New card or changed card input'
  | 'Payment challenge is slow to load'
  | 'Payment challenge Modal Closed'
  | 'Payment challenge Modal Loaded'
  | 'Payment challenge Modal Opened'
  | 'Payment Error'
  | 'Payment intent ID does not match verification ID'
  | 'Payment success'
  | 'Remove item from cart'
  | 'Updated embedded table number'
  | 'Verification Id has the wrong format'
  | 'View bill cart'
  | 'View cart'
  | 'View login page'
  | 'View menu'
  | 'View multi vendor order confirmation'
  | 'View new payment page'
  | 'View order confirmation'
  | 'View payment options'
  | 'View shared payment confirmation'
  | 'View shared payment'
  | 'View SMS code page'
  | 'View venue categories'
  | 'View wallet options'
  | 'Viewed payment confirmation'
  | 'Viewed recent payment notification'

export type LoggerPayload = {
  [key: string]: any
  error?: Error | GraphQLError
  context?: 'Stripe' | 'Payment Page' | 'Cart Total Changed'
}

interface EventLogger {
  logEvent: (event: LoggerEvent, payload: LoggerPayload) => void
}

const consoleLogger: EventLogger = {
  logEvent: (event: LoggerEvent, payload: LoggerPayload) => {
    // eslint-disable-next-line no-console
    console.log(event, payload)
  },
}

export const bugsnagLogger: EventLogger = {
  logEvent: (event: LoggerEvent, payload: LoggerPayload) => {
    if (
      event === 'Verification Id has the wrong format' ||
      event === 'Payment intent ID does not match verification ID' ||
      event === 'Payment Error'
    ) {
      // generally an unexpected log
      if (!payload.error) {
        reportErrorContext(new Error(event), payload.context ?? 'Payment Page')
        // Don't log expected errors
        // manage-api.createOrder returns Payment Error which is mostly just card errors we should ignore
        // should we just ignore graphQL errors in general?
      } else if (
        payload.error.name !== 'UserError' &&
        payload.error.name !== 'Payment Error'
      ) {
        reportErrorContext(payload.error, payload.context ?? 'Payment Page')
      }
    }
  },
}

type LoggerOptions = {
  testLogger?: EventLogger
}
/*
  Centralised logger can be used to log events to multiple providers
  and logs events to console in dev mode.
  @param options {LoggerOptions} pass `testLogger` to be called in test env
  @returns sendLog - fn to send log to all providers
*/
export const useLogger = (options?: LoggerOptions) => {
  const { properties: contextProperties } = useLoggerContext()

  const { amplitudeInstance } = useAmplitude()

  const isConsoleLogEnabled = process.env.NODE_ENV === 'development'

  const eventLoggers = useMemo(() => {
    const loggers: EventLogger[] = []

    if (amplitudeInstance) {
      loggers.push(amplitudeInstance)
    }

    if (isConsoleLogEnabled) {
      loggers.push(consoleLogger)
    }

    if (options?.testLogger && process.env.NODE_ENV === 'test') {
      loggers.push(options.testLogger)
    }

    if (process.env.NODE_ENV !== 'test') {
      loggers.push(bugsnagLogger)
    }

    return loggers
  }, [amplitudeInstance, isConsoleLogEnabled, options?.testLogger])

  const sendLog = useCallback(
    (event: LoggerEvent, payload?: LoggerPayload) => {
      const payloadWithContext = {
        ...contextProperties,
        ...payload,
      }

      eventLoggers.forEach((logger) =>
        logger.logEvent(event, payloadWithContext),
      )
    },
    [eventLoggers, contextProperties],
  )

  return {
    sendLog,
  }
}
